/**
 * @file element-typo.ts
 * @description Цвет и шрифт элемента по частям — `typo` секции v4 (phase19.16,
 * партия SB120, этап 3а «элементы»; site-builder.md SB120, SB8, SB10;
 * проект данных docs/phases/phase19.16-materials/db-design-sb120-stage3.md
 * §2, §6; проект UI uiux-sb120-stage3.md §2, §4–§5).
 *
 * Здесь:
 *   - пределы и схема одной части (`typoPartSchema`): цвет текста, шрифт,
 *     начертание, регистр, размер ПК и телефона, разрядка;
 *   - реестр частей по видам (`TYPO_PART_KEYS`) — постоянный набор ключей:
 *     строгая схема знает, какие части у вида бывают; подсказки кабинета
 *     (font-roles.ts `elementFontParts`) отдают подмножество этих ключей.
 *     Реестр один (SB10): новый вид = одна строка здесь + `typoFor(kind)`
 *     в его схеме;
 *   - правило размера на телефоне (`phoneFontSize`): до 16 px — как на ПК,
 *     крупнее — 8 + ПК/2 (повторяет нынешние H1–H3: 48→32, 32→24, 20→18);
 *   - канон (`compactTypo`): «Авто» и пустые части в базу не пишутся;
 *   - обход своих шрифтов секции (`sectionTypoFonts`) — для ответов API,
 *     проверки при записи, «где используется» и подсчёта семейств.
 *
 * Правило наследования прежнее: нет ключа — «Авто» / «как у роли» / «как
 * было». Цвет текста — `SiteTextColor` (роли текста или свой), не
 * `SiteColorRef`: роли фона текстом не бывают (§2 «два типа цвета»).
 */

import { z } from "zod";

import type { FontRoleTheme } from "../site-theme/font-roles.js";
import { SITE_SYSTEM_FONT_KEY, type SiteFontRegistry } from "../site-theme/fonts.js";
import { siteTextColorSchema } from "../site-theme/text-color.js";
import { ELEMENT_FONT_LIMITS, elementFontSchema, elementFontStyleFields } from "./element-font.js";
import type { SectionElementKind, SectionPayload } from "./schema.js";

/** Пределы части `typo` (uiux-sb120-stage3 §7): размер без шага, вес шагом 100, разрядка шагом 0,01. */
export const ELEMENT_TYPO_LIMITS = {
  size: ELEMENT_FONT_LIMITS.size,
  weight: ELEMENT_FONT_LIMITS.weight,
  tracking: ELEMENT_FONT_LIMITS.tracking,
} as const;

/** Одна часть элемента. Нет ключа — как у роли части (цвет — по фону). */
export const typoPartSchema = z.strictObject({
  /** Цвет текста: роль текста темы или свой; нет ключа — «Авто» по фону. */
  color: siteTextColorSchema.optional(),
  /** Шрифт: роль или свой (`role: "auto"` в базу не пишется — `compactTypo`). */
  font: elementFontSchema.optional(),
  ...elementFontStyleFields,
  /** px до 768; нет ключа — `phoneFontSize(size)` (или размер уровня × шкала, если нет и `size`). */
  sizePhone: z.number().finite().min(ELEMENT_TYPO_LIMITS.size.min).max(ELEMENT_TYPO_LIMITS.size.max).optional(),
});
export type TypoPart = z.infer<typeof typoPartSchema>;

/**
 * Части по видам (db-design-sb120-stage3 §2, таблица «Реестр частей»).
 * Набор не зависит от `layout`, `show` и прочего: кабинет прячет
 * неприменимые части, данные при смене вида не теряются.
 * Этап 3б (добавочно, без версии секции): categories / newsletter `main`,
 * articles — рубрика, название, анонс; reviews — заголовок, текст;
 * announcements — заголовок и текст (у полосы и списка кабинет показывает
 * только текст, ключ `title` хранится).
 */
export const TYPO_PART_KEYS = {
  heading: ["main"],
  eyebrow: ["main"],
  text: ["main"],
  button: ["main"],
  table: ["main"],
  faq: ["main"],
  contacts: ["main"],
  tiles: ["title", "caption"],
  stats: ["number", "label"],
  breadcrumbs: ["main"],
  "product-short": ["main"],
  "product-title": ["title", "category", "rating"],
  "product-description": ["label", "text"],
  // SB120 этап 3б — добавочно (db-design-sb120-stage3 §2, строки «3б»).
  categories: ["main"],
  newsletter: ["main"],
  articles: ["category", "title", "excerpt"],
  reviews: ["title", "text"],
  announcements: ["title", "text"],
} as const satisfies Partial<Record<SectionElementKind, readonly string[]>>;

/** Вид элемента, у которого есть `typo`. */
export type TypoElementKind = keyof typeof TYPO_PART_KEYS;
/** Ключ части вида: `TypoPartKey<"tiles">` = "title" | "caption". */
export type TypoPartKey<K extends TypoElementKind> = (typeof TYPO_PART_KEYS)[K][number];

/** Части надписи на фото (`overlays[].typo`). */
export const OVERLAY_TYPO_PART_KEYS = ["main"] as const;
/** Части шапки раздела (`header.typo`): надпись, заголовок, ссылка справа. */
export const HEADER_TYPO_PART_KEYS = ["eyebrow", "title", "link"] as const;

/** У вида есть `typo`. */
export function isTypoElementKind(kind: unknown): kind is TypoElementKind {
  return typeof kind === "string" && Object.prototype.hasOwnProperty.call(TYPO_PART_KEYS, kind);
}

type TypoShape<P extends readonly string[]> = { [Key in P[number]]: z.ZodOptional<typeof typoPartSchema> };

/** Схема `typo` по набору частей (строгая: лишняя часть — ошибка), сама необязательна. */
export function typoSchemaOf<const P extends readonly string[]>(parts: P) {
  const shape = Object.fromEntries(parts.map((key) => [key, typoPartSchema.optional()])) as TypoShape<P>;
  return z.strictObject(shape).optional();
}

/** Схема `typo` вида элемента. */
export function typoFor<K extends TypoElementKind>(kind: K) {
  return typoSchemaOf(TYPO_PART_KEYS[kind]);
}

/** Значение `typo`: часть → настройки. */
export type TypoValue = Readonly<Record<string, TypoPart | undefined>>;

/**
 * Размер на телефоне по правилу (uiux-sb120-stage3 §2.1): до 16 px — как на
 * ПК, крупнее — 8 + ПК/2. 48→32, 32→24, 20→18, 56→36, 96→56.
 */
export function phoneFontSize(px: number): number {
  return px <= 16 ? px : 8 + px / 2;
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** «Авто» шрифта: `{ kind: "role", role: "auto" }`. */
const isAutoFont = (value: unknown): boolean =>
  isPlainObject(value) && value["kind"] === "role" && value["role"] === "auto";

/**
 * Канон `typo` (§2): снимает `font: { role: "auto" }`, ключи со значением
 * undefined, пустые части `{}` и пустой `typo` (→ undefined). Не объект —
 * как есть (его отвергнет схема). Идемпотентна, вход не меняет.
 */
export function compactTypo<T>(value: T): T | undefined {
  if (!isPlainObject(value)) return value;
  const out: Record<string, unknown> = {};
  for (const [part, raw] of Object.entries(value)) {
    if (raw === undefined) continue;
    if (!isPlainObject(raw)) {
      out[part] = raw;
      continue;
    }
    const kept: Record<string, unknown> = {};
    for (const [key, field] of Object.entries(raw)) {
      if (field === undefined || (key === "font" && isAutoFont(field))) continue;
      kept[key] = field;
    }
    if (Object.keys(kept).length > 0) out[part] = kept;
  }
  return Object.keys(out).length > 0 ? (out as T) : undefined;
}

/** Где в секции лежит `typo`: путь до объекта-владельца и сам владелец. */
type TypoOwnerVisitor = (owner: Record<string, unknown>, path: string) => void;

/** Обход владельцев `typo`: шапка раздела, элементы колонок, надписи на фото. Терпим к форме. */
function forEachTypoOwner(doc: unknown, visit: TypoOwnerVisitor): void {
  if (!isPlainObject(doc)) return;
  if (isPlainObject(doc["header"])) visit(doc["header"], "header");
  const columns = doc["columns"];
  const items = isPlainObject(columns) ? columns["items"] : undefined;
  if (!Array.isArray(items)) return;
  items.forEach((column, col) => {
    const elements = isPlainObject(column) ? column["elements"] : undefined;
    if (!Array.isArray(elements)) return;
    elements.forEach((element, row) => {
      if (!isPlainObject(element)) return;
      const path = `columns.items.${col}.elements.${row}`;
      visit(element, path);
      const overlays = element["overlays"];
      if (!Array.isArray(overlays)) return;
      overlays.forEach((overlay, i) => {
        if (isPlainObject(overlay)) visit(overlay, `${path}.overlays.${i}`);
      });
    });
  });
}

/**
 * Канон всех `typo` секции (чтение — до проверки, запись — writer до
 * записи): в базе нет «Авто» и пустых объектов, `canonicalJson` и «что
 * изменено» не видят ложных правок. Возвращает копию; вход не меняется.
 */
export function compactSectionTypos<T>(doc: T): T {
  if (!isPlainObject(doc)) return doc;
  const copy = JSON.parse(JSON.stringify(doc)) as T;
  forEachTypoOwner(copy, (owner) => {
    if (!("typo" in owner)) return;
    const compact = compactTypo(owner["typo"]);
    if (compact === undefined) delete owner["typo"];
    else owner["typo"] = compact;
  });
  return copy;
}

/** Свой шрифт в секции: путь поля `…typo.<часть>.font` и ключ (каталог или `shop:`). */
export interface SectionTypoFont {
  path: string;
  key: string;
}

/**
 * Свои шрифты секции (§6): `header.typo.*`, `elements[].typo.*`,
 * `overlays[].typo.*` — по порядку обхода. Роли (`kind: "role"`) не
 * входят: это шрифты темы, их грузит оформление.
 */
export function sectionTypoFonts(payload: SectionPayload): SectionTypoFont[] {
  const out: SectionTypoFont[] = [];
  forEachTypoOwner(payload, (owner, path) => {
    const typo = owner["typo"];
    if (!isPlainObject(typo)) return;
    for (const [part, value] of Object.entries(typo)) {
      const font = isPlainObject(value) ? value["font"] : undefined;
      if (isPlainObject(font) && font["kind"] === "custom" && typeof font["font"] === "string") {
        out.push({ path: `${path}.typo.${part}.font`, key: font["font"] });
      }
    }
  });
  return out;
}

/** Ключи своих шрифтов секций без повторов — по первому появлению. */
export function sectionFontKeys(payloads: readonly SectionPayload[]): string[] {
  const keys = new Set<string>();
  for (const payload of payloads) for (const { key } of sectionTypoFonts(payload)) keys.add(key);
  return [...keys];
}

/** Ключи шрифтов ролей темы (заголовки, основной, акцент если выбран) — без повторов. */
export function themeFontKeys(theme: { readonly fonts: FontRoleTheme["fonts"] }): string[] {
  const keys = [theme.fonts.heading, theme.fonts.body, theme.fonts.accent];
  return [...new Set(keys.filter((k): k is string => k !== null))];
}

/**
 * Сколько разных семейств шрифтов грузит страница: переданные ключи (тема,
 * шапка, подвал) плюс свои шрифты элементов секций (uiux-sb120-stage3 §4).
 * С реестром свой шрифт, которого у магазина нет (удалён), не считается —
 * на сайте вместо него шрифт роли. Системный шрифт файлов не грузит.
 */
export function countFontFamilies(
  keys: readonly string[],
  sections: readonly SectionPayload[] = [],
  registry?: SiteFontRegistry,
): number {
  const own = sectionFontKeys(sections).filter((key) => !registry || registry.has(key));
  return new Set([...keys, ...own].filter((k) => k !== "" && k !== SITE_SYSTEM_FONT_KEY)).size;
}
